import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type { DishReport, LossReport, MovementReport } from "../../api/types";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { money } from "../../ui/format";
import { formatQuantity, UNIT_TITLES } from "../../ui/units";

/**
 * Складские отчёты.
 *
 * Три вопроса, ради которых склад и заводят: куда двигался товар, сколько
 * денег ушло мимо продаж и какие блюда действительно зарабатывают. Четвёртый —
 * анализ меню: что из него оставлять.
 */

type Report = "movement" | "losses" | "dishes" | "prices" | "abc";

const REPORTS: { id: Report; title: string }[] = [
  { id: "movement", title: "Движение товара" },
  { id: "losses", title: "Потери и расхождения" },
  { id: "dishes", title: "Маржа по блюдам" },
  { id: "abc", title: "Анализ меню" },
  { id: "prices", title: "Закупочные цены" }
];

interface PriceRow {
  ingredientId: string;
  title: string;
  unit: string;
  supplies: number;
  quantity: string;
  amount: string;
  firstPrice: string;
  lastPrice: string;
  minPrice: string;
  maxPrice: string;
  changePercent: number | null;
  suppliers: string[];
}

interface PriceReport {
  items: PriceRow[];
}

type AbcClass = "A" | "B" | "C";

interface AbcRow {
  productId: string;
  title: string;
  categoryTitle: string;
  quantity: number;
  revenue: number;
  profit: number;
  quantityClass: AbcClass;
  revenueClass: AbcClass;
  profitClass: AbcClass;
  xyz: "X" | "Y" | "Z" | null;
  variation: number | null;
  costIncomplete: boolean;
}

interface AbcReport {
  rows: AbcRow[];
  totals: { quantity: number; revenue: number; profit: number };
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function StockReportsPanel() {
  const { notify } = useToasts();

  const [report, setReport] = useState<Report>("movement");
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(() => isoDay(new Date()));

  const [movement, setMovement] = useState<MovementReport | null>(null);
  const [losses, setLosses] = useState<LossReport | null>(null);
  const [dishes, setDishes] = useState<DishReport | null>(null);
  const [prices, setPrices] = useState<PriceReport | null>(null);
  const [abc, setAbc] = useState<AbcReport | null>(null);
  const [loading, setLoading] = useState(true);

  // Верхняя граница — конец выбранного дня, иначе сегодняшние движения выпадают.
  const query = useMemo(
    () => new URLSearchParams({ from: `${from}T00:00:00`, to: `${to}T23:59:59` }).toString(),
    [from, to]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [movementData, lossData, dishData, priceData, abcData] = await Promise.all([
        api.get<MovementReport>(`/api/admin/stock/reports/movement?${query}`),
        api.get<LossReport>(`/api/admin/stock/reports/losses?${query}`),
        api.get<DishReport>(`/api/admin/stock/reports/dishes?${query}`),
        api.get<PriceReport>(`/api/admin/stock/reports/prices?${query}`),
        api.get<AbcReport>(`/api/admin/stock/reports/abc?${query}`)
      ]);

      setMovement(movementData);
      setLosses(lossData);
      setDishes(dishData);
      setPrices(priceData);
      setAbc(abcData);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить отчёты", "error");
    } finally {
      setLoading(false);
    }
  }, [query, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <section className="panel">
        <header>
          <div>
            <h2>Период</h2>
          </div>
          <div className="row-actions">
            <a className="btn btn-sm" href={`/api/admin/stock/reports/movement.csv?${query}`} download>
              Выгрузить движение
            </a>
            <a className="btn btn-sm" href={`/api/admin/stock/reports/prices.csv?${query}`} download>
              Выгрузить цены
            </a>
            <a className="btn btn-sm" href={`/api/admin/stock/reports/abc.csv?${query}`} download>
              Выгрузить анализ меню
            </a>
          </div>
        </header>

        <div className="filters">
          <label className="field">
            <span>С</span>
            <input type="date" value={from} onChange={event => setFrom(event.target.value)} />
          </label>
          <label className="field">
            <span>По</span>
            <input type="date" value={to} onChange={event => setTo(event.target.value)} />
          </label>
        </div>

        <nav className="tabs" aria-label="Отчёты склада">
          {REPORTS.map(item => (
            <button
              key={item.id}
              type="button"
              className={`tab ${report === item.id ? "active" : ""}`}
              onClick={() => setReport(item.id)}
            >
              {item.title}
            </button>
          ))}
        </nav>
      </section>

      {loading ? <Empty>Считаем…</Empty> : null}

      {!loading && report === "movement" ? <MovementTable data={movement} /> : null}
      {!loading && report === "losses" ? <LossesTable data={losses} /> : null}
      {!loading && report === "dishes" ? <DishesTable data={dishes} /> : null}
      {!loading && report === "prices" ? <PricesTable data={prices} /> : null}
      {!loading && report === "abc" ? <AbcTable data={abc} /> : null}
    </>
  );
}

/**
 * ABC- и XYZ-анализ меню.
 *
 * ABC отвечает, что кормит точку, XYZ — на что можно рассчитывать. Три
 * классификации, а не одна: блюдо-лидер по штукам может не приносить денег,
 * и в таблице это видно только тогда, когда столбцы стоят рядом.
 */
function AbcTable({ data }: { data: AbcReport | null }) {
  if (!data || data.rows.length === 0) return <Empty>За период продаж не было.</Empty>;

  const noXyz = data.rows.every(row => row.xyz === null);

  return (
    <>
      <section className="metrics">
        <div className="metric">
          <b>{data.rows.length}</b>
          <span>позиций продавалось</span>
        </div>
        <div className="metric">
          <b>{data.totals.quantity}</b>
          <span>продано</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.revenue)}</b>
          <span>выручка</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.profit)}</b>
          <span>прибыль</span>
        </div>
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Анализ меню</h2>
            <p>
              A — первые 80 % показателя, B — следующие 15 %, C — хвост. XYZ считается по неделям: X — спрос ровный,
              Y — колеблется, Z — рывками.
              {noXyz ? " Для XYZ нужно хотя бы три недели в периоде — расширьте его, чтобы столбец заполнился." : ""}
            </p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Блюдо</th>
                <th className="num">Продано</th>
                <th>ABC&nbsp;кол-во</th>
                <th className="num">Выручка</th>
                <th>ABC&nbsp;выручка</th>
                <th className="num">Прибыль</th>
                <th>ABC&nbsp;прибыль</th>
                <th>XYZ</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => (
                <tr key={row.productId}>
                  <td>
                    <strong>{row.title}</strong>
                    {row.categoryTitle ? (
                      <>
                        <br />
                        <small className="muted">{row.categoryTitle}</small>
                      </>
                    ) : null}
                    {row.costIncomplete ? (
                      <>
                        <br />
                        {/* Без техкарты прибыль равна выручке, и позиция выглядит выгоднее всех. */}
                        <small className="muted">себестоимость неполная</small>
                      </>
                    ) : null}
                  </td>
                  <td className="num">{row.quantity}</td>
                  <td>{row.quantityClass}</td>
                  <td className="num">{money(row.revenue)}</td>
                  <td>{row.revenueClass}</td>
                  <td className="num">{money(row.profit)}</td>
                  <td>{row.profitClass}</td>
                  <td>
                    {row.xyz ?? "—"}
                    {row.variation === null ? null : (
                      <>
                        {" "}
                        <small className="muted">{row.variation.toFixed(2)}</small>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/**
 * Динамика закупочных цен.
 *
 * Отвечает на вопрос, который иначе задают памятью: «мука ведь была дешевле?».
 * Сортировка по величине изменения, а не по алфавиту: смотреть сюда приходят
 * за тем, что подорожало, а не за полным списком.
 */
function PricesTable({ data }: { data: PriceReport | null }) {
  if (!data || data.items.length === 0) return <Empty>За период закупок не было.</Empty>;

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Закупочные цены</h2>
          <p>
            Цена за единицу учёта по строкам приходных накладных. Средняя себестоимость размазывает
            подорожание по остатку и прячет именно то, что нужно увидеть.
          </p>
        </div>
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th>Закупок</th>
              <th>Первая</th>
              <th>Последняя</th>
              <th>Минимум</th>
              <th>Максимум</th>
              <th>Изменение</th>
              <th>Поставщики</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(row => (
              <tr key={row.ingredientId} className={(row.changePercent ?? 0) > 15 ? "row-alert" : ""}>
                <td>
                  {row.title}
                  <br />
                  <small className="muted">за 1 {UNIT_TITLES[row.unit] ?? row.unit}</small>
                </td>
                <td className="num">{row.supplies}</td>
                <td className="num">{row.firstPrice}</td>
                <td className="num">{row.lastPrice}</td>
                <td className="num">{row.minPrice}</td>
                <td className="num">{row.maxPrice}</td>
                <td className="num">
                  {row.changePercent === null ? (
                    <span className="muted">—</span>
                  ) : (
                    `${row.changePercent > 0 ? "+" : ""}${row.changePercent} %`
                  )}
                </td>
                <td className="tiny">{row.suppliers.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MovementTable({ data }: { data: MovementReport | null }) {
  if (!data || data.items.length === 0) return <Empty>За период движения не было.</Empty>;

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Движение товара</h2>
          <p>Остаток на начало плюс приход минус расход даёт остаток на конец — строки сходятся по определению.</p>
        </div>
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th className="num">На начало</th>
              <th className="num">Приход</th>
              <th className="num">Продажи</th>
              <th className="num">Списано</th>
              <th className="num">Заготовки</th>
              <th className="num">Инвентаризация</th>
              <th className="num">На конец</th>
              <th className="num">Себестоимость продаж</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(row => (
              <tr key={row.ingredientId}>
                <td>
                  <strong>{row.title}</strong>
                  <br />
                  <small className="muted">{row.groupTitle ?? "без группы"}</small>
                </td>
                <td className="num">{formatQuantity(row.opening, row.unit)}</td>
                <td className="num">{row.supply ? formatQuantity(row.supply, row.unit) : "—"}</td>
                <td className="num">{row.sale ? formatQuantity(row.sale, row.unit) : "—"}</td>
                <td className="num">{row.writeoff ? formatQuantity(row.writeoff, row.unit) : "—"}</td>
                <td className="num">{row.production ? formatQuantity(row.production, row.unit) : "—"}</td>
                <td className={`num ${row.inventory < 0 ? "text-crit" : ""}`}>
                  {row.inventory ? formatQuantity(row.inventory, row.unit) : "—"}
                </td>
                <td className="num">
                  <strong>{formatQuantity(row.closing, row.unit)}</strong>
                </td>
                <td className="num">{money(row.saleCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LossesTable({ data }: { data: LossReport | null }) {
  if (!data) return null;

  const losses = Math.abs(data.totals.shortage) + data.totals.writeoffCost;

  return (
    <>
      <section className="metrics">
        <div className="metric">
          <b>{money(data.totals.saleCost)}</b>
          <span>себестоимость продаж</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.writeoffCost)}</b>
          <span>списано</span>
        </div>
        <div className={`metric ${data.totals.shortage < 0 ? "metric-alert" : ""}`}>
          <b>{money(Math.abs(data.totals.shortage))}</b>
          <span>недостача</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.surplus)}</b>
          <span>излишки</span>
        </div>
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Потери и расхождения</h2>
            <p>
              Недостача — разница между тем, что должно было остаться по техкартам, и тем, что нашли на полке.
              Пара процентов от расхода — обычная погрешность взвешивания; десятки процентов означают, что врут либо
              карты, либо руки.
              {data.totals.saleCost > 0 ? (
                <>
                  {" "}
                  Всего потерь за период: <strong>{money(losses)}</strong> — это{" "}
                  {((losses / data.totals.saleCost) * 100).toFixed(1)} % от себестоимости продаж.
                </>
              ) : null}
            </p>
          </div>
        </header>

        {data.items.length === 0 ? (
          <Empty>За период не было ни списаний, ни расхождений.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Себестоимость продаж</th>
                  <th className="num">Списано</th>
                  <th className="num">Расхождение</th>
                  <th className="num">Доля потерь</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(row => (
                  <tr key={row.ingredientId} className={row.inventoryCost < 0 ? "row-alert" : ""}>
                    <td>
                      <strong>{row.title}</strong>
                    </td>
                    <td className="num">{money(row.saleCost)}</td>
                    <td className="num">{row.writeoffCost ? money(row.writeoffCost) : "—"}</td>
                    <td className="num">{row.inventoryCost ? money(row.inventoryCost) : "—"}</td>
                    <td className="num">{row.lossShare === null ? "—" : `${row.lossShare} %`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function DishesTable({ data }: { data: DishReport | null }) {
  if (!data || data.rows.length === 0) return <Empty>За период продаж не было.</Empty>;

  return (
    <>
      <section className="metrics">
        <div className="metric">
          <b>{money(data.totals.revenue)}</b>
          <span>выручка</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.cost)}</b>
          <span>себестоимость</span>
        </div>
        <div className="metric">
          <b>{money(data.totals.margin)}</b>
          <span>маржа</span>
        </div>
        <div className="metric">
          <b>{data.totals.foodCost === null ? "—" : `${data.totals.foodCost} %`}</b>
          <span>фудкост</span>
        </div>
      </section>

      <section className="panel">
        <header>
          <div>
            <h2>Маржа по блюдам</h2>
            <p>
              Себестоимость — оценка по текущим техкартам и текущим средним ценам, а не факт. Настоящий расход лежит в
              движениях по номенклатуре: сыр из одного куска ушёл и в бургер, и в соус, и разложить его обратно по
              блюдам нельзя.
              {data.totals.uncosted > 0 ? (
                <>
                  {" "}
                  Выручка без техкарт: <strong>{money(data.totals.uncosted)}</strong> — она не участвует в фудкосте.
                </>
              ) : null}
            </p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Блюдо</th>
                <th className="num">Продано</th>
                <th className="num">Выручка</th>
                <th className="num">Себестоимость</th>
                <th className="num">Маржа</th>
                <th className="num">Фудкост</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => (
                <tr key={row.slug}>
                  <td>
                    <strong>{row.title}</strong>
                    {!row.hasRecipe ? (
                      <>
                        <br />
                        <small className="muted">нет техкарты</small>
                      </>
                    ) : null}
                  </td>
                  <td className="num">{row.quantity}</td>
                  <td className="num">{money(row.revenue)}</td>
                  <td className="num">{row.totalCost === null ? "—" : money(row.totalCost)}</td>
                  <td className="num">{row.margin === null ? "—" : money(row.margin)}</td>
                  <td className="num">{row.foodCost === null ? "—" : `${row.foodCost} %`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
